import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { SupplierFinding, SupplierProduct } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { CriticitaBadge } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard } from '../components/common'
import { BadgeStatoContratto, etichettaTipoContratto, formattaImporto } from '../components/contratti'
import { DataTable, type Column } from '../components/DataTable'
import { FinestraFornitore, etichettaSostituibilita, etichettaTipoFornitore } from '../components/Fornitori'
import { ValutazioniFornitore } from '../components/ValutazioniFornitore'
import { DocumentiFornitore } from '../components/DocumentiFornitore'
import { SbomCenso } from '../components/SbomCenso'
import { FinestraNuovaRichiesta, TabellaDichiarazioni, TabellaRichieste } from '../components/RichiesteFornitore'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * The page of a supplier (/suppliers/:id): what of theirs runs here and
 * where, the open findings of their products (exploited first) and the end of
 * support, their hardware and cloud, the Business Apps they directly support,
 * contracts and assets, and the names the registry knows them by. The tab is
 * in the URL (`?tab=`). See supplier_service.
 */

type Scheda = 'scheda' | 'prodotti' | 'vulnerabilita' | 'richieste' | 'dichiarazioni' | 'documenti' | 'valutazioni' | 'infrastruttura' | 'contratti' | 'nomi'

const n = (x: number | undefined) => (x ?? 0).toLocaleString(localeCorrente())

export default function FornitoreDettaglio() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const scheda = (params.get('tab') ?? 'scheda') as Scheda
  const permessi = usePermessiModuli()
  const scrive = permessi.scrive('suppliers')
  const dati = useAsync(() => api.supplier(id), [id])
  const [modifica, setModifica] = useState(false)
  const [chiedi, setChiedi] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const richieste = useAsync(() => (scheda === 'richieste' ? api.supplierRequests(id) : Promise.resolve([])), [id, scheda])

  if (dati.loading && !dati.data) return <Spinner />
  if (!dati.data) return <ErrorBanner error={dati.error} />
  const s = dati.data
  const fp = s.footprint

  async function elimina() {
    if (!window.confirm(t('Eliminare il fornitore «{nome}»? I suoi nomi tornano fra le proposte; contratti e asset perdono il collegamento, non il testo del fornitore.', { nome: s.name }))) return
    try {
      await api.deleteSupplier(s.id)
      navigate('/suppliers/list')
    } catch (err) {
      setErrore(err)
    }
  }

  const schede: [Scheda, string][] = [
    ['scheda', t('Scheda')],
    ['prodotti', t('Prodotti in uso')],
    ['vulnerabilita', t('Vulnerabilità ({n})', { n: fp.findings ?? 0 })],
    ['richieste', t('Richieste')],
    ['dichiarazioni', t('Dichiarazioni')],
    ['documenti', t('Documenti')],
    ['valutazioni', t('Valutazioni e certificazioni')],
    ['infrastruttura', t('Hardware, sistemi e cloud')],
    ['contratti', t('Contratti e asset')],
    ['nomi', t('Nomi ({n})', { n: s.aliases.length })],
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <p className="field__hint" style={{ margin: 0 }}><Link to="/suppliers/list">{t('Fornitori')}</Link></p>
          <h1 className="page__title">
            {s.name} {s.status === 'ceased' && <span className="badge badge--muted">{t('cessato')}</span>}
            {s.system && <span className="badge badge--info">{t('gestito da CENSO')}</span>}
          </h1>
          <p className="page__subtitle">
            {s.kinds.map(etichettaTipoFornitore).join(' · ') || t('tipo non indicato')}
            {s.country ? ` · ${s.country}` : ''}{' · '}<CriticitaBadge value={s.criticality} />
            {s.criticality_override && <span className="text-muted"> ({t('scelta a mano')})</span>}
          </p>
        </div>
        {scrive && (
          <div className="toolbar">
            <button type="button" onClick={() => setChiedi(true)}>{t('Chiedi al fornitore')}</button>
            <button type="button" className="secondary" onClick={() => setModifica(true)}>{t('Modifica')}</button>
            {!s.system && <button type="button" className="secondary" onClick={() => void elimina()}>{t('Elimina')}</button>}
          </div>
        )}
      </header>
      <ErrorBanner error={errore} />
      <div className="stats">
        <StatCard label={t('CI')} value={n(fp.cis)} hint={t('dove gira qualcosa di suo')} onClick={() => setParams({ tab: 'infrastruttura' })} />
        <StatCard label={t('Installazioni')} value={n(fp.installations)} onClick={() => setParams({ tab: 'prodotti' })} />
        <StatCard label={t('Reperti aperti')} value={n(fp.findings)} tone={fp.findings ? 'warn' : 'ok'} onClick={() => setParams({ tab: 'vulnerabilita' })} />
        <StatCard label={t('Sfruttate attivamente')} value={n(fp.exploited)} tone={fp.exploited ? 'error' : 'ok'}
                  hint={t('CISA KEV o ENISA EUVD')} onClick={() => setParams({ tab: 'vulnerabilita' })} />
        <StatCard label={t('Fuori supporto')} value={n(fp.end_of_support)} tone={fp.end_of_support ? 'warn' : 'ok'}
                  onClick={() => setParams({ tab: 'vulnerabilita' })} />
        <StatCard label={t('Contratti in vigore')} value={n(fp.active_contracts)}
                  hint={fp.annual_value ? t('{v} l’anno', { v: formattaImporto(fp.annual_value, 'EUR') }) : undefined}
                  onClick={() => setParams({ tab: 'contratti' })} />
      </div>
      <nav className="tabs" role="tablist">
        {schede.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={scheda === k}
                  className={`tabs__tab${scheda === k ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ tab: k })}>{l}</button>
        ))}
      </nav>
      {scheda === 'scheda' && s.system && (
        <>
          <p className="field__hint">
            {t('Gestito da CENSO: è chi produce CENSO, la sua sonda e il suo agente. Ne tiene aggiornati SBOM e VEX a ogni versione (scheda Documenti); i dati della scheda, le valutazioni e i contratti sono tuoi.')}
          </p>
          <SbomCenso />
        </>
      )}
      {scheda === 'scheda' && <SchedaDati />}
      {scheda === 'prodotti' && <Prodotti id={s.id} />}
      {scheda === 'vulnerabilita' && <Vulnerabilita id={s.id} />}
      {scheda === 'infrastruttura' && <Collegati id={s.id} parte="infrastruttura" />}
      {scheda === 'contratti' && <Collegati id={s.id} parte="contratti" />}
      {scheda === 'nomi' && <Nomi />}
      {scheda === 'richieste' && (
        <Card>
          {!s.security_contact && <p className="field__hint" style={{ marginTop: 0 }}>{t('Manca il contatto di sicurezza del fornitore: aggiungilo nella scheda, o scrivi il destinatario nella richiesta.')}</p>}
          {richieste.loading && !richieste.data ? <Spinner /> : (
            <TabellaRichieste righe={richieste.data ?? []} conFornitore={false} scrive={scrive} onCambiato={() => void richieste.reload()} />
          )}
        </Card>
      )}
      {scheda === 'dichiarazioni' && <Card><TabellaDichiarazioni supplierId={s.id} scrive={scrive} /></Card>}
      {scheda === 'valutazioni' && <ValutazioniFornitore supplierId={s.id} scrive={scrive} />}
      {scheda === 'documenti' && <DocumentiFornitore supplierId={s.id} scrive={scrive} />}
      {chiedi && (
        <FinestraNuovaRichiesta supplierId={s.id} contatto={s.security_contact}
                                onChiudi={(inviata) => { setChiedi(false); if (inviata) setParams({ tab: 'richieste' }) }} />
      )}
      {modifica && (
        <FinestraFornitore esistente={s} onAnnulla={() => setModifica(false)}
                           onFatto={() => { setModifica(false); void dati.reload() }} />
      )}
    </>
  )

  function SchedaDati() {
    const righe: [string, string | JSX.Element][] = [
      [t('Ragione sociale'), s.legal_name || '—'],
      [t('Partita IVA'), s.vat_number || '—'],
      [t('LEI'), s.lei || '—'],
      [t('Sito'), s.website ? <a href={s.website.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer">{s.website}</a> : '—'],
      [t('Contatto di sicurezza'), s.security_contact || <span className="badge badge--warn">{t('manca')}</span>],
      [t('Pagina degli avvisi di sicurezza'), s.psirt_url && /^https?:\/\//.test(s.psirt_url)
        ? <a href={s.psirt_url} target="_blank" rel="noreferrer">{s.psirt_url}</a> : s.psirt_url || '—'],
      [t('Avvisi CSAF'), s.csaf_url || '—'],
      [t('Referente interno'), [s.owner, s.owner_email].filter(Boolean).join(' · ') || '—'],
      [t('Criticità'), <CriticitaBadge key="c" value={s.criticality} />],
      [t('Business App sostenute'), (fp.business_apps ?? []).join(', ') || '—'],
      [t('Fornitore ICT (DORA)'), s.is_ict ? t('sì') : t('no')],
      [t('Sostituibilità'), s.substitutability ? etichettaSostituibilita(s.substitutability) : '—'],
      [t('Strategia di uscita'), s.exit_strategy ? `${s.exit_strategy}${s.exit_tested ? ` (${t('provata')})` : ''}` : '—'],
      [t('Sedi dei dati'), s.data_locations.join(', ') || '—'],
      [t('CI critici dove è solo installato'), n(fp.incidental_cis)],
      [t('Note'), s.notes || '—'],
    ]
    return (
      <Card>
        <dl className="kv">
          {righe.map(([k, v]) => [<dt key={`${k}-t`}>{k}</dt>, <dd key={`${k}-d`}>{v}</dd>])}
        </dl>
        <p className="field__hint">
          {t('La criticità viene da ciò che il fornitore sostiene direttamente: il suo software quando è un elemento di una Business App, il sistema operativo, l’hardware e il cloud dei CI che sono interi in una Business App o in un business service. Un programma solo installato su un server critico non rende critico chi lo pubblica.')}
        </p>
      </Card>
    )
  }

  function Nomi() {
    return (
      <Card>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('I nomi con cui CENSO riconosce questo fornitore nel software installato, nei contratti e negli asset, con le scritture viste. Stacca un nome che non è di questo fornitore: torna fra le proposte al prossimo catalogo.')}
        </p>
        {s.aliases.length === 0 ? <EmptyState title={t('Nessun nome')} /> : (
          <ul className="elenco-semplice">
            {s.aliases.map((a) => (
              <li key={a.id}>
                <code>{a.key}</code> — {a.labels.slice(0, 6).join(' · ')}
                {Object.keys(a.sources).length > 0 && <span className="text-muted"> ({Object.entries(a.sources).map(([f, k]) => `${f} ${n(k)}`).join(', ')})</span>}
                {scrive && (
                  <>{' '}<button type="button" className="link" onClick={() => void api.detachSupplierAlias(s.id, a.id).then(() => dati.reload()).catch(setErrore)}>{t('Stacca')}</button></>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    )
  }
}

function Prodotti({ id }: { id: string }) {
  const dati = useAsync(() => api.supplierProducts(id), [id])
  const [aperto, setAperto] = useState<SupplierProduct | null>(null)
  const installazioni = useAsync(() => (aperto ? api.supplierInstallations(id, aperto.name, aperto.version) : Promise.resolve([])), [aperto])
  const colonne: Column<SupplierProduct>[] = [
    { key: 'name', header: t('Programma'), render: (p) => <button type="button" className="link" onClick={() => setAperto(p)}>{p.name}</button>, value: (p) => p.name },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'cis', header: t('CI'), value: (p) => p.cis },
    { key: 'installations', header: t('Installazioni'), value: (p) => p.installations },
  ]
  return (
    <Card>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (dati.data?.items ?? []).length === 0 ? (
        <EmptyState title={t('Nessun programma')} hint={t('I programmi si riconoscono dal produttore scritto nell’inventario software (agente, sonda, cloud).')} />
      ) : (
        <>
          {dati.data && dati.data.total > dati.data.items.length && (
            <p className="field__hint">{t('I primi {n} di {tot}: usa la ricerca per gli altri.', { n: dati.data.items.length, tot: dati.data.total })}</p>
          )}
          <DataTable rows={dati.data?.items ?? []} columns={colonne} rowKey={(p) => `${p.name}|${p.version}`} conteggio={{ nome: t('programmi') }} />
        </>
      )}
      {aperto && (
        <Card title={t('{nome} {versione}: dove è installato', { nome: aperto.name, versione: aperto.version })}
              actions={<button type="button" className="secondary" onClick={() => setAperto(null)}>{t('Chiudi')}</button>}>
          {installazioni.loading ? <Spinner /> : (
            <ul className="elenco-semplice">
              {(installazioni.data ?? []).map((c) => <li key={c.ci_id}><Link to={`/cis/${c.ci_id}`}>{c.ci_name}</Link> <span className="text-muted">{c.ci_type}</span></li>)}
            </ul>
          )}
        </Card>
      )}
    </Card>
  )
}

function Vulnerabilita({ id }: { id: string }) {
  const dati = useAsync(() => api.supplierFindings(id), [id])
  const colonne: Column<SupplierFinding>[] = [
    { key: 'exploited', header: t('Sfruttata'), value: (f) => (f.exploited ? 1 : 0),
      render: (f) => (f.exploited ? <span className="badge badge--error">{t('sfruttata')}</span> : '') },
    { key: 'severity', header: t('Gravità'), render: (f) => t(f.severity), value: (f) => f.severity },
    { key: 'title', header: t('Reperto'), render: (f) => (
      <>
        {f.title}
        {f.end_of_support && <> <span className="badge badge--warn">{t('fuori supporto')}</span></>}
        {f.supplier_statements && <> <span className="badge badge--info">{t('{k} di {n} CVE escluse dal fornitore', { k: f.supplier_statements.excluded, n: f.supplier_statements.total })}</span></>}
        <div className="text-muted eagle-piccolo mono">{f.rule}</div>
      </>
    ), value: (f) => `${f.title} ${f.rule}` },
    { key: 'package', header: t('Prodotto'), value: (f) => `${f.package} ${f.version}` },
    { key: 'ci_name', header: t('CI'), render: (f) => <Link to={`/cis/${f.ci_id}#sicurezza`}>{f.ci_name}</Link>, value: (f) => f.ci_name ?? '' },
  ]
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I reperti aperti sui prodotti di questo fornitore installati qui: vulnerabilità dei pacchetti e dei programmi, fine supporto, aggiornamenti mancanti del sistema, firmware. Quelli coperti da un’eccezione (rischio accettato, supporto esteso da contratto) non compaiono.')}
      </p>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : (dati.data ?? []).length === 0 ? (
        <EmptyState title={t('Nessun reperto aperto')} />
      ) : (
        <DataTable rows={dati.data ?? []} columns={colonne} rowKey={(f) => f.id} conteggio={{ nome: t('reperti') }} />
      )}
    </Card>
  )
}

function Collegati({ id, parte }: { id: string; parte: 'infrastruttura' | 'contratti' }) {
  const dati = useAsync(() => api.supplierRelated(id), [id])
  if (dati.loading && !dati.data) return <Spinner />
  if (!dati.data) return <ErrorBanner error={dati.error} />
  const r = dati.data
  const elencoCi = (righe: typeof r.hardware, totale: number) => righe.length === 0 ? <p className="text-muted">—</p> : (
    <>
      <ul className="elenco-semplice">
        {righe.map((c) => <li key={c.ci_id}><Link to={`/cis/${c.ci_id}`}>{c.ci_name || c.ci_id}</Link> <span className="text-muted">{c.ci_type}</span></li>)}
      </ul>
      {totale > righe.length && <p className="field__hint">{t('I primi {n} di {tot}.', { n: righe.length, tot: totale })}</p>}
    </>
  )
  if (parte === 'infrastruttura') {
    return (
      <>
        <Card title={t('Hardware ({n})', { n: r.hardware_total })}>{elencoCi(r.hardware, r.hardware_total)}</Card>
        <Card title={t('Sistemi operativi ({n})', { n: r.operating_systems_total })}>{elencoCi(r.operating_systems, r.operating_systems_total)}</Card>
        <Card title={t('Cloud ({n})', { n: r.cloud_total })}>{elencoCi(r.cloud, r.cloud_total)}</Card>
      </>
    )
  }
  return (
    <>
      <Card title={t('Contratti')}>
        {r.contracts_hidden ? <p className="text-muted">{t('Il tuo ruolo non vede i contratti.')}</p>
          : r.contracts.length === 0 ? <p className="text-muted">{t('Nessun contratto collegato a questo fornitore.')}</p> : (
          <ul className="elenco-semplice">
            {r.contracts.map((c) => (
              <li key={c.id}>
                <Link to={`/contracts/${c.id}`}><strong>{c.reference}</strong></Link>{c.name ? ` · ${c.name}` : ''} · {etichettaTipoContratto(c.contract_type)}{' '}
                <BadgeStatoContratto stato={c.effective_status} />
                {c.end_date && <span className="text-muted"> · {new Date(`${c.end_date}T00:00:00`).toLocaleDateString(localeCorrente())}</span>}
                {c.annual_value > 0 && <span className="text-muted"> · {formattaImporto(c.annual_value, c.currency)}</span>}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title={t('Asset forniti')}>
        {r.assets.length === 0 ? <p className="text-muted">—</p> : (
          <ul className="elenco-semplice">
            {r.assets.map((a) => <li key={a.id}><Link to={`/assets/${a.id}`}>{a.asset_tag}</Link>{a.name ? ` · ${a.name}` : ''}</li>)}
          </ul>
        )}
      </Card>
      {r.cra_products.length > 0 && (
        <Card title={t('Prodotti CRA di cui è il fabbricante')}>
          <ul className="elenco-semplice">
            {r.cra_products.map((p) => <li key={p.id}><Link to={`/cra/products/${p.id}`}>{p.name}</Link></li>)}
          </ul>
        </Card>
      )}
    </>
  )
}
